import { useState, useEffect } from 'react';
import { Card, Badge, Toggle, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import styles from './Profile.module.css';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: string;
  phone?: string;
  license_number?: string;
  bio?: string;
}

interface LoginEntry {
  id: string;
  ip_address: string;
  user_agent: string;
  status: string;
  created_at: string;
}

const roleLabel: Record<string, string> = {
  tenant_owner: '管理者', tenant_manager: 'マネージャー', tenant_staff: 'スタッフ',
};

function parseBrowser(ua: string): string {
  if (!ua || ua === 'unknown') return '不明';
  const browser = /Edg/.test(ua) ? 'Edge' : /Chrome/.test(ua) ? 'Chrome' : /Safari/.test(ua) ? 'Safari' : /Firefox/.test(ua) ? 'Firefox' : '不明';
  const os = /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'macOS' : /iPhone/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : '';
  return `${browser}${os ? ' / ' + os : ''}`;
}

export function Profile() {
  const { data: profile, loading, refetch } = useApi<UserProfile>('/tenant/users/me');
  const { data: loginHistory } = useApi<LoginEntry[]>('/tenant/users/me/login-history');
  const { data: notifPrefsFetched } = useApi<Record<string, boolean>>('/tenant/users/me/notifications');
  const { data: tenantConfig } = useApi<{ company_name: string }>('/tenant/config');
  const [form, setForm] = useState({ lastName: '', firstName: '', email: '', phone: '', license_number: '', bio: '' });
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [notifications, setNotifications] = useState({
    inquiry: true, my_customer_inquiry: true, signature: true, portal_error: true, checkin_alert: true, days_limit: true, stale_contact: true, contract_expiring: true, trial_expiring: true, email_notify: false,
  });

  // サーバーから取得した通知設定を state に反映
  useEffect(() => {
    if (notifPrefsFetched) {
      setNotifications(prev => ({ ...prev, ...notifPrefsFetched }));
    }
  }, [notifPrefsFetched]);

  // 通知設定を切り替えてサーバーに保存
  const handleNotificationToggle = async (key: string, value: boolean) => {
    const next = { ...notifications, [key]: value };
    setNotifications(next); // 楽観的更新
    try {
      await api('/tenant/users/me/notifications', { method: 'PUT', body: JSON.stringify({ [key]: value }) });
      toast('通知設定を保存しました', 'success');
    } catch (e: any) {
      // エラー時はロールバック
      setNotifications(prev => ({ ...prev, [key]: !value }));
      toast(e?.message || '通知設定の保存に失敗しました', 'error');
    }
  };

  useEffect(() => {
    if (profile) {
      const names = profile.name.split(/\s+/);
      setForm({
        lastName: names[0] || '', firstName: names.slice(1).join(' ') || '',
        email: profile.email || '', phone: profile.phone || '',
        license_number: profile.license_number || '', bio: profile.bio || '',
      });
    }
  }, [profile]);

  const handleSave = async () => {
    const name = `${form.lastName} ${form.firstName}`.trim();
    if (!name) { toast('姓は必須です', 'error'); return; }
    try {
      await api('/tenant/users/me', { method: 'PATCH', body: JSON.stringify({
        name, email: form.email, phone: form.phone || null,
        license_number: form.license_number || null, bio: form.bio || null,
      })});
      toast('プロフィールを保存しました', 'success');
      refetch();
    } catch { toast('保存に失敗しました', 'error'); }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current || !pwForm.newPw) { toast('パスワードを入力してください', 'error'); return; }
    if (pwForm.newPw !== pwForm.confirm) { toast('新しいパスワードが一致しません', 'error'); return; }
    if (pwForm.newPw.length < 8) { toast('パスワードは8文字以上で入力してください', 'error'); return; }
    try {
      await api('/tenant/users/me/change-password', { method: 'POST', body: JSON.stringify({
        current_password: pwForm.current, new_password: pwForm.newPw,
      })});
      toast('パスワードを変更しました', 'success');
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (e) { toast(e instanceof Error ? e.message : 'パスワード変更に失敗しました', 'error'); }
  };

  if (loading) return <div className={styles.page}>読み込み中...</div>;

  const companyName = tenantConfig?.company_name ?? '';

  return (
    <div className={styles.page}>
      {/* マイプロフィール */}
      <Card title="👤 マイプロフィール" actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={styles.btnOutline} onClick={() => { if (profile) { const names = profile.name.split(/\s+/); setForm({ lastName: names[0] || '', firstName: names.slice(1).join(' ') || '', email: profile.email || '', phone: profile.phone || '', license_number: profile.license_number || '', bio: profile.bio || '' }); } }}>キャンセル</button>
          <button className={styles.btnPrimary} onClick={handleSave}>保存する</button>
        </div>
      }>
        {/* アバター */}
        <div className={styles.avatarRow}>
          <div className={styles.avatarWrap}>
            <div className={styles.avatar}>{form.lastName.charAt(0) || '?'}</div>
            <button className={styles.avatarEdit} onClick={() => toast('写真をアップロードします', 'info')}>✏️</button>
          </div>
          <div>
            <div className={styles.profileName}>{form.lastName} {form.firstName}</div>
            <div className={styles.profileRole}>{roleLabel[profile?.role ?? ''] ?? profile?.role}・宅建士 ｜ {companyName}</div>
            <div className={styles.profileEmail}>{form.email}</div>
          </div>
        </div>

        {/* フォーム */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}><label className={styles.formLabel}>姓</label><input className={styles.formInput} value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} /></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>名</label><input className={styles.formInput} value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} /></div>
        </div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}><label className={styles.formLabel}>メールアドレス</label><input className={styles.formInput} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>電話番号</label><input className={styles.formInput} type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
        </div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>役職</label>
            <div className={styles.roleDisplay}><Badge variant="blue">{roleLabel[profile?.role ?? ''] ?? profile?.role}</Badge></div>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>宅建士証番号</label><input className={styles.formInput} value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })} placeholder="東京都第 123456号" /></div>
        </div>
        <div className={styles.formGroup}><label className={styles.formLabel}>自己紹介・担当エリア</label>
          <textarea className={styles.formTextarea} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} rows={3} placeholder="担当エリアや得意分野を記入..." />
        </div>
      </Card>

      {/* 通知設定 */}
      <Card title="🔔 通知設定">
        <div style={{ fontSize: 11, color: 'var(--ink3)', marginBottom: 8, fontWeight: 600 }}>全般（全社イベント）</div>
        {[
          { key: 'inquiry', label: '新規問い合わせ（全社）', desc: '社内の誰かが顧客を登録したとき／OTA・ポータル経由の問い合わせ' },
          { key: 'signature', label: '電子署名の更新', desc: '契約書の署名が完了・保留になったとき' },
          { key: 'portal_error', label: 'ポータル入稿エラー', desc: 'SUUMO・HOME\'S等への入稿でエラーが発生したとき' },
          { key: 'checkin_alert', label: '民泊チェックイン前アラート', desc: 'チェックイン2時間前に通知' },
          { key: 'days_limit', label: '180日上限アラート', desc: '残り30日・10日・5日で通知' },
          { key: 'trial_expiring', label: 'トライアル期限通知', desc: 'トライアル期間の残り3日・1日前／期限切れ時（毎朝9時）' },
        ].map(item => (
          <div key={item.key} className={styles.notifRow}>
            <div>
              <div className={styles.notifLabel}>{item.label}</div>
              <div className={styles.notifDesc}>{item.desc}</div>
            </div>
            <Toggle checked={notifications[item.key as keyof typeof notifications]} onChange={v => handleNotificationToggle(item.key, v)} />
          </div>
        ))}

        <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 16, marginBottom: 8, fontWeight: 600 }}>営業通知（あなたの担当）</div>
        {[
          { key: 'my_customer_inquiry', label: '担当顧客の問い合わせ', desc: '自分が担当の顧客に新規問い合わせ・更新があったとき' },
          { key: 'stale_contact', label: '未連絡顧客アラート', desc: '3日以上対応履歴がない担当顧客がいるとき（毎朝9時）' },
          { key: 'contract_expiring', label: '契約期限前通知', desc: '担当契約の期限 30日前 / 7日前（毎朝9時）' },
        ].map(item => (
          <div key={item.key} className={styles.notifRow}>
            <div>
              <div className={styles.notifLabel}>{item.label}</div>
              <div className={styles.notifDesc}>{item.desc}</div>
            </div>
            <Toggle checked={notifications[item.key as keyof typeof notifications]} onChange={v => handleNotificationToggle(item.key, v)} />
          </div>
        ))}

        <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 16, marginBottom: 8, fontWeight: 600 }}>受信方法</div>
        {[
          { key: 'email_notify', label: 'メールでも受け取る', desc: '上記の通知をメールでも受信する' },
        ].map(item => (
          <div key={item.key} className={styles.notifRow}>
            <div>
              <div className={styles.notifLabel}>{item.label}</div>
              <div className={styles.notifDesc}>{item.desc}</div>
            </div>
            <Toggle checked={notifications[item.key as keyof typeof notifications]} onChange={v => handleNotificationToggle(item.key, v)} />
          </div>
        ))}
      </Card>

      {/* パスワード変更 */}
      <Card title="🔐 パスワード変更">
        <div className={styles.formGroup}><label className={styles.formLabel}>現在のパスワード</label><input className={styles.formInput} type="password" value={pwForm.current} onChange={e => setPwForm({ ...pwForm, current: e.target.value })} placeholder="••••••••" /></div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}><label className={styles.formLabel}>新しいパスワード</label><input className={styles.formInput} type="password" value={pwForm.newPw} onChange={e => setPwForm({ ...pwForm, newPw: e.target.value })} placeholder="8文字以上" /></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>新しいパスワード（確認）</label><input className={styles.formInput} type="password" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} placeholder="もう一度入力" /></div>
        </div>
        <button className={styles.btnPrimary} onClick={handleChangePassword}>パスワードを更新する</button>
      </Card>

      {/* 内見カレンダー連携 (Phase 2) */}
      <IcsCalendarCard />

      {/* ログイン履歴 */}
      <Card title="🔍 最近のログイン履歴">
        <table className={styles.table}>
          <thead><tr><th className={styles.th}>日時</th><th className={styles.th}>IPアドレス</th><th className={styles.th}>ブラウザ</th><th className={styles.th}>ステータス</th></tr></thead>
          <tbody>
            {(loginHistory ?? []).length > 0 ? (loginHistory ?? []).map(h => (
              <tr key={h.id}>
                <td className={styles.td} style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{new Date(h.created_at).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                <td className={styles.td} style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{h.ip_address}</td>
                <td className={styles.td} style={{ color: 'var(--ink2)' }}>{parseBrowser(h.user_agent)}</td>
                <td className={styles.td}><Badge variant={h.status === 'success' ? 'green' : 'red'}>{h.status === 'success' ? '成功' : '失敗'}</Badge></td>
              </tr>
            )) : (
              <tr><td className={styles.td} colSpan={4} style={{ textAlign: 'center', color: 'var(--ink4)' }}>ログイン履歴はありません</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function IcsCalendarCard() {
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const user = useAuthStore(s => s.user);
  useEffect(() => {
    api<{ token: string | null }>('/tenant/users/me/ics-token').then(r => setToken(r.token)).catch(() => setToken(null));
  }, []);
  const icsUrl = token && user?.tenantSlug
    ? `${window.location.origin}/api/public/viewings.ics?token=${encodeURIComponent(token)}&slug=${encodeURIComponent(user.tenantSlug)}`
    : '';
  const issue = async () => {
    setLoading(true);
    try {
      const r = await api<{ token: string }>('/tenant/users/me/ics-token', { method: 'POST' });
      setToken(r.token);
      toast('カレンダー URL を発行しました', 'success');
    } catch (e) { toast((e as Error)?.message || '発行に失敗しました', 'error'); }
    finally { setLoading(false); }
  };
  const revoke = async () => {
    if (!confirm('現在のURLを無効化しますか?既存のカレンダー連携は切れます')) return;
    setLoading(true);
    try {
      await api('/tenant/users/me/ics-token', { method: 'DELETE' });
      setToken(null);
      toast('URL を無効化しました', 'success');
    } catch { toast('無効化に失敗しました', 'error'); }
    finally { setLoading(false); }
  };
  const copy = () => {
    if (!icsUrl) return;
    navigator.clipboard.writeText(icsUrl).then(() => toast('URL をコピーしました', 'success'));
  };
  return (
    <Card title="📅 内見カレンダー連携">
      <div style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.7, marginBottom: 10 }}>
        Google カレンダー / Outlook で「URL からカレンダーを追加」するとあなたの担当する内見予定が自動で同期されます。更新頻度はカレンダーアプリ側の仕様に依存します（Google: 6〜24時間 / Outlook: 30分〜 / Apple: 5分〜、Outlook・Apple は設定可）。
      </div>
      {token ? (
        <div>
          <div style={{ fontSize: 11, color: 'var(--ink3)', marginBottom: 4 }}>サブスクライブ URL:</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input readOnly value={icsUrl} className={styles.formInput} style={{ fontFamily: 'var(--mono)', fontSize: 11 }} />
            <button className={styles.btnPrimary} onClick={copy}>コピー</button>
          </div>
          <div style={{ marginTop: 10 }}>
            <button className={styles.btnOutline} onClick={revoke} disabled={loading}>URL を無効化</button>
          </div>
        </div>
      ) : (
        <button className={styles.btnPrimary} onClick={issue} disabled={loading}>
          {loading ? '発行中...' : '📅 カレンダー URL を発行'}
        </button>
      )}
    </Card>
  );
}
